import { emptySplitApi } from "./emptyApi"

/**
 * Admin Ad Blast Reports — the launch tracker read from the `ad-blast` audit
 * history across every company (super-admin only). Mirrors the backend
 * `AdBlastReportDto` / `CompanyAdLaunchGoalDto`.
 */

/** One target ad set of a launch line, with its outcome. */
export interface AdBlastReportAdSet {
  adSetId: string
  adSetName?: string
  campaignName?: string
  adId?: string
  /** Current synced status of the created ad (ACTIVE, PAUSED, …). */
  adStatus?: string
  success: boolean
  error?: string
}

/** One launch-tracker line: a single ad launched by one ad-blast into one ad set (failed attempts are not reported). */
export interface AdBlastReportLine {
  id: string
  auditId: string
  jobRunId: string | null
  companyId: string
  companyName: string
  /** ISO datetime the blast completed. */
  launchedAt: string
  createdBy: string
  createdVia: string
  /** Live | Paused | Failed | Launched | … */
  status: string
  /** Always "Launched" — failed attempts are not reported. */
  qa: string
  /** The single target ad set of this line. */
  adSets: AdBlastReportAdSet[]
  /** `Campaign//AdSet` of the target, like the sheet. */
  campaignAdSets: string
  adName: string
  landingPage: string
  postId: string
  previewUrl: string
  channel: string
  type: string
  adsCreated: number
  adsFailed: number
  notes: string
}

export interface AdBlastReportCompany {
  id: string
  name: string
}

export interface AdBlastReport {
  from: string
  to: string
  generatedAt: string
  /** Companies on an enabled ad-blast-report schedule roster, plus any with a line in the window. */
  companies: AdBlastReportCompany[]
  lines: AdBlastReportLine[]
  truncated: boolean
}

export interface AdBlastReportArgs {
  /** ISO datetime (inclusive). */
  from: string
  /** ISO datetime (inclusive). */
  to: string
  companyIds?: string[]
}

export interface AdLaunchGoal {
  low: number
  high: number
}

export interface CompanyAdLaunchGoal extends AdLaunchGoal {
  companyId: string
  companyName: string
}

export const adBlastReportsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getAdBlastReport: builder.query<AdBlastReport, AdBlastReportArgs>({
      query: ({ from, to, companyIds }) => ({
        url: "ad-blast-reports",
        params: {
          from,
          to,
          ...(companyIds?.length ? { companyIds: companyIds.join(",") } : {}),
        },
      }),
    }),
    listAdLaunchGoals: builder.query<CompanyAdLaunchGoal[], void>({
      query: () => "ad-blast-reports/goals",
      providesTags: ["AdLaunchGoals"],
    }),
    setAdLaunchGoal: builder.mutation<
      CompanyAdLaunchGoal,
      { companyId: string } & AdLaunchGoal
    >({
      query: ({ companyId, ...body }) => ({
        url: `ad-blast-reports/goals/${companyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdLaunchGoals"],
    }),
    clearAdLaunchGoal: builder.mutation<{ deleted: boolean }, string>({
      query: companyId => ({
        url: `ad-blast-reports/goals/${companyId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdLaunchGoals"],
    }),
  }),
})

export const {
  useGetAdBlastReportQuery,
  useListAdLaunchGoalsQuery,
  useSetAdLaunchGoalMutation,
  useClearAdLaunchGoalMutation,
} = adBlastReportsApi
